import React, { useId } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { easeOut } from '../utils/motion';
type DrawnUnderlineProps = {
  children: React.ReactNode;
  /** Seconds to wait before the stroke starts painting. */
  delay?: number;
};

// Filled brush body: hairline-thin at the left, swelling to a thick, rounded end at the right.
// Follows a gentle upward arc that peaks near the middle.
const brushBody = 'M2 13.7 C 50 11, 100 8, 150 7.6 C 200 7.2, 260 7.5, 296 9.5 Q 302 14, 295 18.5 C 260 16.5, 205 13.5, 150 12.4 C 100 12.6, 50 13.8, 2 14.3 Z';

// Thin dry-brush streaks along the thicker right half.
const streaks = [{
  d: 'M140 8.6 C 190 7.6, 245 8, 290 10.4',
  width: 1.4,
  opacity: 0.5
}, {
  d: 'M165 14 C 210 15.2, 250 16.6, 288 18.6',
  width: 1.2,
  opacity: 0.4
}];

/** Wraps inline text with a tapered, paintbrush-style underline that paints itself in on mount. */
export function DrawnUnderline({
  children,
  delay = 0.6
}: DrawnUnderlineProps) {
  const reduceMotion = useReducedMotion();
  const uid = useId().replace(/:/g, '');
  const filterId = `brush-${uid}`;
  const clipId = `brush-clip-${uid}`;
  return <span className="relative inline-block whitespace-nowrap" data-id="element-75" data-mp="boitvlwi1vcf">
      {children}
      <svg aria-hidden="true" viewBox="0 0 300 22" preserveAspectRatio="none" className="pointer-events-none absolute -bottom-[0.5em] left-0 h-[0.68em] w-full overflow-visible text-positive" data-id="element-76" data-mp="3t2ei0z52kxpq">
        <defs data-id="element-77" data-mp="3k4m1xsrixm1a">
          <filter id={filterId} x="-5%" y="-60%" width="110%" height="220%" data-id="element-78" data-mp="3o3l63b5flero">
            <feTurbulence type="fractalNoise" baseFrequency="0.9 0.35" numOctaves="2" seed="7" result="noise" data-id="element-79" data-mp="3o1i5jx6nk38p" />
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" xChannelSelector="R" yChannelSelector="G" data-id="element-80" data-mp="3f355qajzinr1" />
          </filter>
          <clipPath id={clipId} data-id="element-81" data-mp="18dqi51oi6ik1">
            <motion.rect x="-4" y="-6" height="30" initial={reduceMotion ? false : {
            width: 0
          }} animate={{
            width: 310
          }} transition={{
            duration: 0.6,
            ease: easeOut,
            delay
          }} data-id="element-82" data-mp="18di1qzt68v43" />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`} data-id="element-83" data-mp="16xmho14z3qnu">
          <g filter={`url(#${filterId})`} fill="currentColor" data-id="element-84" data-mp="11vx34bijkuv0">
            <path d={brushBody} fillOpacity={0.9} data-id="element-85" data-mp="11y9tnd2d8djo" />
            {streaks.map(s => <path key={s.d} d={s.d} fill="none" stroke="currentColor" strokeWidth={s.width} strokeOpacity={s.opacity} strokeLinecap="round" vectorEffect="non-scaling-stroke" data-id="element-86" data-mp="wzgfrjj62kiz" />)}
          </g>
        </g>
      </svg>
    </span>;
}